import { Component, computed, inject, input, signal } from "@angular/core";
import { Router } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import { ChartConfiguration } from "chart.js";

import { PercentageValuePipe, TranslateTokenPipe } from "../../../../core/pipes";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import type {
  EkpiPerformanceModelType,
  MasterEkpiActuals,
  PillarPerformanceModelType,
  SelectedPeriodModelType,
} from "../../../models";
import { LanguageService, PillarEkpiPerformanceService } from "../../../services";
import { ChartActionButtonsComponent } from "../../chart-action-buttons/chart-action-buttons";
import { EkpiInfoComponent, ekpiValue } from "./_ekpi-info";
import { PILLAR_RING, PillarPerformanceGraphComponent } from "./_pillar-performance-graph";

type PillarTooltip = {
  name: string;
  value: string;
  color: string;
  pct: number;
  x: number;
  y: number;
  below: boolean;
};

/* Matches .pillar-tooltip in the stylesheet; the anchor needs the box size to
   clamp against the viewport and to flip below when there is no room above. */
const TOOLTIP = { width: 196, height: 74, margin: 10, gap: 6 };

@Component({
  selector: "adaa-government-enabler-pillars",
  templateUrl: "./government-enabler-pillars.html",
  styleUrl: "./government-enabler-pillars.scss",
  imports: [
    TranslatePipe,
    TranslateTokenPipe,
    PercentageValuePipe,
    EkpiInfoComponent,
    PillarPerformanceGraphComponent,
    ChartActionButtonsComponent,
  ],
})
export class GovernmentEnablerPillarsComponent {
  private readonly _pillarEkpiPerformanceService = inject(PillarEkpiPerformanceService);
  private _router = inject(Router);
  readonly languageService = inject(LanguageService);

  period = input.required<SelectedPeriodModelType>();

  data = signal<MasterEkpiActuals | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chartData = signal<ChartConfiguration<"bar">["data"] | any | undefined>(undefined);

  pillars = computed(() => this._pillarEkpiPerformanceService.ekpiPillarPerformance());
  periodLabel = computed(() => {
    const selected = this.period();
    if (selected.type === "year") return `${selected.period.year}`;
    return `Q${selected.period.quarter} ${selected.period.year}`;
  });
  elementIds = computed(() => {
    const count = this._pillarEkpiPerformanceService.ekpiPillarPerformance().length;
    const ids: string[] = [];
    for (let index = 0; index < count; index++) {
      ids.push("enablerChart" + index);
    }
    return ids;
  });
  chartTitles = computed(() => {
    const field = AdaaHelper.getFieldLanguage("pillarName");
    const pillars = this._pillarEkpiPerformanceService.ekpiPillarPerformance();
    return pillars.map((pillar) => pillar[field as keyof PillarPerformanceModelType] as string);
  });

  extraContent = signal<string[]>([]);
  activeEkpi = signal<{ pillarId: number; ekpiId: number } | null>(null);
  tooltip = signal<PillarTooltip | null>(null);

  public pillarColor(pillar: PillarPerformanceModelType) {
    if (!AdaaHelper.isDefined(pillar.pillarPerformance)) {
      return Constants.COLOR_CODES[Constants.CONSTANT_COLORS.EVALUATION_LIGHTGRAY];
    }
    return Constants.COLOR_CODES[pillar.evaluation ?? Constants.CONSTANT_COLORS.EVALUATION_GRAY];
  }

  public setContent(content: string) {
    this.extraContent().push(content);
  }

  public activeEkpiId(pillarId: number) {
    const active = this.activeEkpi();
    return active?.pillarId === pillarId ? active.ekpiId : null;
  }

  public hoverEkpi(pillar: PillarPerformanceModelType, ekpiId: number | null, ring: HTMLElement) {
    if (ekpiId === null) {
      this.activeEkpi.set(null);
      this.tooltip.set(null);
      return;
    }

    const index = pillar.ekpiList.findIndex(({ id }) => id === ekpiId);
    if (index === -1) return;

    this.activeEkpi.set({ pillarId: pillar.id, ekpiId });
    this.tooltip.set(this._buildTooltip(pillar.ekpiList[index], index, pillar.ekpiList.length, ring));
  }

  /* Anchor the tooltip on the mid-angle of the hovered arc so a row hover and a
     segment hover land in the same place, then keep it inside the viewport. */
  private _buildTooltip(ekpi: EkpiPerformanceModelType, index: number, count: number, ring: HTMLElement) {
    const rect = ring.getBoundingClientRect();
    const angle = ((index + 0.5) / count) * 2 * Math.PI;
    const outerRadius = (rect.width - 2 * PILLAR_RING.padding) / 2;
    const ratio = rect.width ? (outerRadius * (1 + PILLAR_RING.cutout)) / 2 / rect.width : 0;

    const anchorX = rect.left + rect.width * (0.5 + ratio * Math.sin(angle));
    const anchorY = rect.top + rect.height * (0.5 - ratio * Math.cos(angle)) - TOOLTIP.gap;
    const below = anchorY - TOOLTIP.height - TOOLTIP.margin < 0;
    const half = TOOLTIP.width / 2;

    const isPercentage = ekpi.measurementUnit === Constants.MEASUREMENT.PERCENTAGE;

    return {
      name: AdaaHelper.getItemValueByToken(ekpi, "ekpiName"),
      value: ekpiValue(ekpi),
      color: Constants.COLOR_CODES[ekpi.evaluation ?? Constants.CONSTANT_COLORS.EVALUATION_GRAY],
      pct: isPercentage ? Math.max(4, Math.min(100, ekpi.actual ?? 0)) : 100,
      x: Math.max(half + TOOLTIP.margin, Math.min(window.innerWidth - half - TOOLTIP.margin, anchorX)),
      y: below ? anchorY + 2 * TOOLTIP.gap + TOOLTIP.margin : anchorY,
      below,
    };
  }

  public goToEkpi(ekpiId: number) {
    this._router.navigateByUrl(`/console/kpi/ekpi/${ekpiId}?periodId=${this.period().period.id}`);
  }
}
